import { TGraphQLResponse } from '@/shared/redux/api/base-api.types';
import { handleRedirect } from '@/shared/redux/api/utils/handle-redirect';

type BatchedResult = {
  response: Response;
  jsonResult: TGraphQLResponse;
};

type PendingQuery = {
  body: { query: string; variables?: Record<string, any> };
  resolve: (result: BatchedResult) => void;
  reject: (error: Error) => void;
};

const BATCH_WINDOW_MS = 10;

const batchQueues = new Map<string, PendingQuery[]>();
const batchTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Enqueues a GraphQL query for batching. Queries to the same URL are
 * collected within a short time window and sent as a single HTTP request.
 *
 * Returns both the Response object (for HTTP status/redirect checks)
 * and the parsed JSON result.
 */
export const batchedFetch = (
  url: string,
  body: { query: string; variables?: Record<string, any> },
  headers: Record<string, string>,
): Promise<BatchedResult> => {
  return new Promise<BatchedResult>((resolve, reject) => {
    let queue = batchQueues.get(url);
    if (!queue) {
      queue = [];
      batchQueues.set(url, queue);
    }

    queue.push({ body, resolve, reject });

    const existingTimer = batchTimers.get(url);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    batchTimers.set(
      url,
      setTimeout(() => flushBatch(url, headers), BATCH_WINDOW_MS),
    );
  });
};

const fetchSingle = async (
  url: string,
  entry: PendingQuery,
  headers: Record<string, string>,
): Promise<void> => {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(entry.body),
    });
    handleRedirect(response);
    const jsonResult: TGraphQLResponse = await response.json();
    entry.resolve({ response, jsonResult });
  } catch (error) {
    entry.reject(error as Error);
  }
};

const fallbackToIndividual = async (
  url: string,
  queue: PendingQuery[],
  headers: Record<string, string>,
): Promise<void> => {
  await Promise.all(queue.map((entry) => fetchSingle(url, entry, headers)));
};

const flushBatch = async (
  url: string,
  headers: Record<string, string>,
): Promise<void> => {
  const queue = batchQueues.get(url);
  batchTimers.delete(url);
  batchQueues.delete(url);

  if (!queue || queue.length === 0) return;

  // Single query - send without batching
  if (queue.length === 1) {
    await fetchSingle(url, queue[0], headers);
    return;
  }

  // Multiple queries - send as batch (JSON array)
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(queue.map((entry) => entry.body)),
    });
    handleRedirect(response);

    // If batch request failed (backend may not support batching),
    // fall back to sending each query individually
    if (!response.ok) {
      await fallbackToIndividual(url, queue, headers);
      return;
    }

    const results: TGraphQLResponse[] = await response.json();

    if (!Array.isArray(results)) {
      await fallbackToIndividual(url, queue, headers);
      return;
    }

    results.forEach((result, index) => {
      if (index < queue.length) {
        queue[index].resolve({ response, jsonResult: result });
      }
    });
  } catch (error) {
    queue.forEach((entry) => entry.reject(error as Error));
  }
};
